import { useState, useEffect } from 'react'
import { supabase } from './supabase'
import {
  Order,
  OrderItem,
  OrderItemOption,
  KitchenStation,
  VInventoryDashboard,
  VInventoryAlert,
  InventoryMovement,
  VMenuProfitability,
} from './types'

export interface OrderWithDetails extends Order {
  items: (OrderItem & { options: OrderItemOption[] })[]
}

export function useAdminOrders(restaurantId: string | undefined) {
  const [orders, setOrders] = useState<OrderWithDetails[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!restaurantId) return

    async function fetchOrders() {
      try {
        setLoading(true)
        const { data: ordersData, error: ordersError } = await supabase
          .from('orders')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .order('created_at', { ascending: false })
          .limit(50)

        if (ordersError) throw ordersError

        if (!ordersData || ordersData.length === 0) {
          setOrders([])
          return
        }

        const orderIds = ordersData.map((o) => o.id)

        const { data: itemsData, error: itemsError } = await supabase
          .from('order_items')
          .select('*')
          .in('order_id', orderIds)

        if (itemsError) throw itemsError

        const itemIds = itemsData?.map((i) => i.id) || []

        let optionsData: OrderItemOption[] = []
        if (itemIds.length > 0) {
          const { data: optData, error: optError } = await supabase
            .from('order_item_options')
            .select('*')
            .in('order_item_id', itemIds)

          if (optError) throw optError
          optionsData = optData || []
        }

        const assembledOrders = ordersData.map((order) => {
          const orderItems = (itemsData || []).filter(
            (item) => item.order_id === order.id,
          )
          const itemsWithOptions = orderItems.map((item) => ({
            ...item,
            options: optionsData.filter((opt) => opt.order_item_id === item.id),
          }))

          return {
            ...order,
            items: itemsWithOptions,
          }
        })

        setOrders(assembledOrders)
      } catch (error) {
        console.error('Error fetching admin orders:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchOrders()

    const interval = setInterval(fetchOrders, 30000)
    return () => clearInterval(interval)
  }, [restaurantId])

  // ─── Order-level RPC actions ───────────────────────────────
  const refetchOrders = async () => {
    try {
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('created_at', { ascending: false })
        .limit(50)
      if (ordersError) throw ordersError
      if (!ordersData || ordersData.length === 0) {
        setOrders([])
        return
      }
      const orderIds = ordersData.map((o) => o.id)
      const { data: itemsData } = await supabase
        .from('order_items')
        .select('*')
        .in('order_id', orderIds)
      const itemIds = itemsData?.map((i) => i.id) || []
      let optionsData: OrderItemOption[] = []
      if (itemIds.length > 0) {
        const { data: optData } = await supabase
          .from('order_item_options')
          .select('*')
          .in('order_item_id', itemIds)
        optionsData = optData || []
      }
      const assembled = ordersData.map((order) => {
        const orderItems = (itemsData || []).filter(
          (item) => item.order_id === order.id,
        )
        return {
          ...order,
          items: orderItems.map((item) => ({
            ...item,
            options: optionsData.filter((opt) => opt.order_item_id === item.id),
          })),
        }
      })
      setOrders(assembled)
    } catch (err) {
      console.error('[NOIZA] refetchOrders error:', err)
    }
  }

  const acceptOrder = async (orderId: string) => {
    try {
      console.log('[NOIZA] acceptOrder:', orderId)
      const { error } = await supabase.rpc('accept_order', {
        p_order_id: orderId,
      })
      if (error) throw error
      await refetchOrders()
    } catch (err) {
      console.error('[NOIZA] acceptOrder error:', err)
    }
  }

  const startOrderPreparation = async (orderId: string) => {
    try {
      console.log('[NOIZA] startOrderPreparation:', orderId)
      const { error } = await supabase.rpc('start_order_preparation', {
        p_order_id: orderId,
      })
      if (error) throw error
      await refetchOrders()
    } catch (err) {
      console.error('[NOIZA] startOrderPreparation error:', err)
    }
  }

  const markOrderReady = async (orderId: string) => {
    try {
      console.log('[NOIZA] markOrderReady:', orderId)
      const { error } = await supabase.rpc('mark_order_ready', {
        p_order_id: orderId,
      })
      if (error) throw error
      await refetchOrders()
    } catch (err) {
      console.error('[NOIZA] markOrderReady error:', err)
    }
  }

  const deliverOrder = async (orderId: string) => {
    try {
      console.log('[NOIZA] deliverOrder:', orderId)
      const { error } = await supabase.rpc('deliver_order', {
        p_order_id: orderId,
      })
      if (error) throw error
      await refetchOrders()
    } catch (err) {
      console.error('[NOIZA] deliverOrder error:', err)
    }
  }

  const cancelOrder = async (orderId: string) => {
    try {
      console.log('[NOIZA] cancelOrder:', orderId)
      const { error } = await supabase.rpc('cancel_order', {
        p_order_id: orderId,
      })
      if (error) throw error
      await refetchOrders()
    } catch (err) {
      console.error('[NOIZA] cancelOrder error:', err)
    }
  }

  // Legacy — kept for backward compatibility (dashboard uses this)
  const updateOrderStatus = async (orderId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status })
        .eq('id', orderId)
      if (error) throw error
      await refetchOrders()
    } catch (error) {
      console.error('Error updating order status:', error)
    }
  }

  // ─── Item-level RPC actions ────────────────────────────────
  const markItemPreparing = async (itemId: string) => {
    try {
      console.log('[NOIZA] markItemPreparing:', itemId)
      const { error } = await supabase.rpc('mark_order_item_preparing', {
        p_order_item_id: itemId,
      })
      if (error) throw error
      await refetchOrders()
    } catch (err) {
      console.error('[NOIZA] markItemPreparing error:', err)
    }
  }

  const markItemReady = async (itemId: string) => {
    try {
      console.log('[NOIZA] markItemReady:', itemId)
      const { error } = await supabase.rpc('mark_order_item_ready', {
        p_order_item_id: itemId,
      })
      if (error) throw error
      // Also sync order status from items
      // Find which order this item belongs to
      const parentOrder = orders.find((o) =>
        o.items.some((i) => i.id === itemId),
      )
      if (parentOrder) {
        try {
          await supabase.rpc('sync_order_status_from_items', {
            p_order_id: parentOrder.id,
          })
        } catch (syncErr) {
          console.error('[NOIZA] syncOrderFromItems error:', syncErr)
        }
      }
      await refetchOrders()
    } catch (err) {
      console.error('[NOIZA] markItemReady error:', err)
    }
  }

  // Legacy item status update (kept for backward compat)
  const updateItemStatus = async (
    itemId: string,
    status: string,
    orderId: string,
  ) => {
    try {
      const { error } = await supabase
        .from('order_items')
        .update({ status })
        .eq('id', itemId)
      if (error) throw error
      await refetchOrders()
    } catch (error) {
      console.error('[NOIZA] Error updating item status:', error)
    }
  }

  return {
    orders,
    loading,
    updateOrderStatus,
    updateItemStatus,
    acceptOrder,
    startOrderPreparation,
    markOrderReady,
    deliverOrder,
    cancelOrder,
    markItemPreparing,
    markItemReady,
    refetchOrders,
  }
}

export function useKitchenStations(restaurantId: string | undefined) {
  const [stations, setStations] = useState<KitchenStation[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!restaurantId) return

    async function fetchStations() {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('kitchen_stations')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .eq('is_active', true)
          .order('sort_order')

        if (error) throw error
        setStations(data || [])
      } catch (error) {
        console.error('Error fetching stations:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchStations()
  }, [restaurantId])

  return { stations, loading }
}

export function useInventoryData(restaurantId: string | undefined) {
  const [inventory, setInventory] = useState<VInventoryDashboard[]>([])
  const [alerts, setAlerts] = useState<VInventoryAlert[]>([])
  const [movements, setMovements] = useState<InventoryMovement[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!restaurantId) return

    async function fetchData() {
      try {
        setLoading(true)
        const [invRes, alertsRes] = await Promise.all([
          supabase
            .from('v_inventory_dashboard')
            .select('*')
            .eq('restaurant_id', restaurantId),
          supabase
            .from('v_inventory_alerts')
            .select('*')
            .eq('restaurant_id', restaurantId),
        ])

        if (invRes.data) setInventory(invRes.data)
        if (alertsRes.data) setAlerts(alertsRes.data)

        // Fetch recent movements
        if (invRes.data && invRes.data.length > 0) {
          const itemIds = invRes.data.map((i) => i.inventory_item_id)
          const { data: movData } = await supabase
            .from('inventory_movements')
            .select('*')
            .eq('restaurant_id', restaurantId)
            .in('inventory_item_id', itemIds)
            .order('created_at', { ascending: false })
            .limit(20)

          if (movData) setMovements(movData)
        }
      } catch (error) {
        console.error('Error fetching inventory:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [restaurantId])

  return { inventory, alerts, movements, loading }
}

export function useMetricsData(restaurantId: string | undefined) {
  const [profitability, setProfitability] = useState<VMenuProfitability[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!restaurantId) return

    async function fetchMetrics() {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('v_menu_profitability')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .order('margin_percent', { ascending: false })

        if (error) throw error
        setProfitability(data || [])
      } catch (error) {
        console.error('Error fetching metrics:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchMetrics()
  }, [restaurantId])

  return { profitability, loading }
}
